import {convertMToMm} from '../../../utils/unit';
import type {
    SketchDimensionSnapshot,
    SketchDimensionUnitOverride,
} from '../SketchDimensionTypes';

/**
 * 尺寸显示文字格式化（02 计划 §10.1 `SketchDimensionTextFormatter` 职责）。
 *
 * 设计说明：
 * 1. 单位边界统一走 `utils/unit.ts`：核心米/弧度 → 显示单位（默认毫米/度，
 *    可被 `display.unitOverride` 单尺寸覆盖，VAL-004）；
 * 2. `overrideValue` 只覆盖显示文本，不写回参数与求解（DISP-002），且几何
 *    变化不自动更新覆盖文本；
 * 3. 显示样式互转只改前缀（径向 R/直径 ⌀/线性无前缀），不改求解参数（DISP-001）；
 * 4. driven（参考）尺寸按 SOLIDWORKS 默认语义加括号；
 * 5. 公差为对称/双侧偏差后缀（±x 或 +a/-b，核心单位换算到显示单位）。
 */

/** 英寸每米。 */
const INCHES_PER_METER = 1000 / 25.4;

/** 长度显示单位换算（核心米 → 显示单位）。 */
const LENGTH_UNIT_FACTORS: Readonly<Record<string, {readonly factor: number; readonly suffix: string}>> = {
    mm: {factor: 1000, suffix: 'mm'},
    cm: {factor: 100, suffix: 'cm'},
    m: {factor: 1, suffix: 'm'},
    in: {factor: INCHES_PER_METER, suffix: 'in'},
};

/** 解析显示精度（小数位，默认 2，钳制 0-8）。 */
function resolvePrecision(snapshot: SketchDimensionSnapshot): number {
    const precision = snapshot.display.precision;

    if (precision === undefined || !Number.isInteger(precision)) {
        return 2;
    }

    return Math.min(Math.max(precision, 0), 8);
}

/** 把核心单位值换算为显示单位文本（不含公差）。 */
function formatCoreValue(
    value: number,
    isAngle: boolean,
    unitOverride: SketchDimensionUnitOverride | undefined,
    precision: number,
): string {
    if (isAngle) {
        if (unitOverride === 'rad') {
            return `${value.toFixed(precision)}rad`;
        }

        return `${(value * 180 / Math.PI).toFixed(precision)}°`;
    }

    const unit = LENGTH_UNIT_FACTORS[unitOverride ?? 'mm'] ?? LENGTH_UNIT_FACTORS.mm!;

    // 毫米路径统一走 utils/unit.ts 的转换边界，其余单位由毫米派生。
    const displayValue = unit.suffix === 'mm'
        ? convertMToMm(value)
        : convertMToMm(value) * (unit.factor / 1000);

    return `${displayValue.toFixed(precision)}${unit.suffix}`;
}

/**
 * 格式化尺寸显示值。
 *
 * @param snapshot 尺寸快照。
 * @returns 显示文本（如 `60.00mm`、`R12.50mm`、`(45.00°)`、`60.00mm ±0.10mm`）。
 */
export function formatSketchDimensionDisplayValue(snapshot: SketchDimensionSnapshot): string {
    const display = snapshot.display;

    // Override 只覆盖文本：不换算、不追加公差，几何变化也不自动更新（DISP-002）。
    if (typeof display.overrideValue === 'string' && display.overrideValue.length > 0) {
        return snapshot.role === 'driven' ? `(${display.overrideValue})` : display.overrideValue;
    }

    const value = snapshot.role === 'driven' && snapshot.measuredValue !== null
        ? snapshot.measuredValue
        : snapshot.parameter.nominalValue;
    const isAngle = snapshot.parameter.quantityKind === 'angle';
    const precision = resolvePrecision(snapshot);
    /**
     * 显示样式前缀（DISP-001 核心/显示分离）：
     * - 未覆盖 displayStyle 时按核心类型默认（radius→R、diameter→⌀）；
     * - 覆盖后按显示样式互转前缀，求解参数不变。
     */
    const style = display.displayStyle
        ?? (snapshot.dimensionKind === 'diameter'
            ? 'diametric'
            : snapshot.dimensionKind === 'radius' ? 'radial' : 'linear');
    const prefix = isAngle ? '' : style === 'diametric' ? '⌀' : style === 'radial' ? 'R' : '';
    let text = `${prefix}${formatCoreValue(value, isAngle, display.unitOverride, precision)}`;

    // 公差后缀：对称 ± / 双侧 +a -b（显示单位与主值一致）。
    if (display.tolerancePlus !== undefined || display.toleranceMinus !== undefined) {
        const plus = display.tolerancePlus ?? 0;
        const minus = display.toleranceMinus ?? 0;

        if (plus === minus) {
            text += ` ±${formatCoreValue(plus, isAngle, display.unitOverride, precision)}`;
        } else {
            text += ` +${formatCoreValue(plus, isAngle, display.unitOverride, precision)}`
                + ` -${formatCoreValue(minus, isAngle, display.unitOverride, precision)}`;
        }
    }

    return snapshot.role === 'driven' ? `(${text})` : text;
}
